import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';

/**
 * Row model for `app-selected-item-list`. Callers that need the original
 * domain object (e.g. an option group) can attach it via `source` instead of
 * looking the row up again by index.
 */
export interface SelectedItem<T = unknown> {
  id: string | number;
  name: string;
  source?: T;
}

/**
 * The selected-items list that appears under a multi-select — the same bordered
 * checkbox rows a merchant sees when picking option groups on an offer.
 *
 * The checkbox is the remove control. The name beside it is plain text, so
 * clicking the label cannot drop the selection.
 *
 * Optional `#itemLabel` / `#itemActions` / `#itemBody` templates let a caller
 * (offer option groups) project extra chrome without a second copy of the row.
 * When `#itemBody` is set, each row is wrapped in a `mat-expansion-panel`.
 *
 * The caller owns the state: this component renders exactly what `items` says
 * and never mutates it, so the list and whatever control feeds it stay in sync
 * by both reading the same source.
 *
 * <b>OnPush, and what callers owe it.</b> One `@Input`, one `@Output`, no
 * injected state and no internal mutation — a presentational component in
 * `SharedModule`, so it re-renders only when `items` arrives as a NEW array
 * reference. A caller that rebuilds the array on every change-detection pass
 * (a plain getter doing `filter`/`map`, say) works but wastes the strategy; a
 * caller that MUTATES the array in place will not re-render and must replace it
 * instead. `discountvoucher-form.component.ts` memoises its getter on the
 * selection for exactly this reason.
 */
@Component({
  selector: 'app-selected-item-list',
  templateUrl: './selected-item-list.component.html',
  styleUrls: ['./selected-item-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectedItemListComponent {
  @Input() items: SelectedItem[] = [];
  /** Focused when the last row is removed (the multi-select above the list). */
  @Input() fallbackFocusId?: string;
  @Output() itemRemoved = new EventEmitter<SelectedItem>();

  @ContentChild('itemLabel') itemLabelTpl?: TemplateRef<unknown>;
  @ContentChild('itemActions') itemActionsTpl?: TemplateRef<unknown>;
  @ContentChild('itemBody') itemBodyTpl?: TemplateRef<unknown>;

  constructor(private host: ElementRef<HTMLElement>) {}

  trackById(_index: number, item: SelectedItem): string | number {
    return item.id;
  }

  onCheckboxChange(event: Event, item: SelectedItem): void {
    event.preventDefault();
    event.stopPropagation();
    const index = this.items.findIndex(
      (candidate) => String(candidate.id) === String(item.id),
    );
    const removingLast = this.items.length === 1;
    this.itemRemoved.emit(item);
    if (removingLast) {
      this.focusFallback();
      return;
    }
    setTimeout(() => this.focusAt(index));
  }

  private focusAt(index: number): void {
    const inputs = this.host.nativeElement.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"]',
    );
    if (inputs.length) {
      inputs[Math.min(index, inputs.length - 1)].focus();
      return;
    }
    this.focusFallback();
  }

  private focusFallback(): void {
    if (this.fallbackFocusId) {
      document.getElementById(this.fallbackFocusId)?.focus();
    }
  }
}
